/**
 * A SAJÁT NÉZŐ — kép és PDF canvasra rajzolva (#64, 3. lépés).
 *
 * MIÉRT SAJÁT, és nem a böngésző PDF-nézője egy `<iframe>`-ben. A #64
 * jelölőit A PAPÍRRA kell tűzni, és a böngésző nézője zárt doboz: nem mondja
 * meg, melyik oldalon áll, mennyire nagyított, merre forgatott — tehát egy
 * jelölő nem tudná, hová kerüljön. A saját nézőben ez mind a miénk, és a
 * kép meg a PDF ugyanúgy viselkedik.
 *
 * A BILLENTYŰK A PAPÍRON ÉLNEK, nem a munkalistán. A munkalista rövidítései
 * (`J`/`K`, `Enter`, a Tensoft-választó `0`–`9`-e) mindenhol érvényesek; a
 * néző billentyűi csak akkor, ha a fókusz a papíron áll — és amit a néző
 * lekezel, azt nem engedi tovább, különben egy `0` egyszerre illesztene ÉS
 * választaná a „egyik sem" Tensoft-jelöltet.
 *
 * ═══ A JELÖLŐK (#64, 4. lépés) ═══
 *
 * A jelölő a lap FÖLÖTT áll, egy a canvasszal azonos méretű rétegen, és a
 * helye relatív — a nagyítás így nem mozdítja el. A forgatás igen: a hely a
 * dokumentum SAJÁT álló helyzetében tárolódik, és a rajzoláskor fordul a
 * képernyőre (`fromPagePoint`), letételkor vissza (`toPagePoint`).
 *
 * LETÉTEL MODAL NÉLKÜL. `N` (vagy a „Jegyzet" gomb) → célkereszt a látható
 * rész közepén; kattintás vagy nyilak + `Enter` teszi le, az `Esc` elveti.
 * A jegyzet mezője a Tételsorokban nyílik — ott áll a sor, ami a jelölő.
 *
 * A JOBB KLIKK SZÁNDÉKOSAN NINCS BEKÖTVE. A vászon „Jegyzet ide" menüt
 * rajzolt rá, de egyetlen pontos menü egy kattintással több, mint a gomb +
 * kattintás — és a böngésző saját menüjét (kép mentése) venné el.
 */

import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { embedType } from '../original';
import type { DocumentFile } from '../useDocumentFile';
import { printBlob, saveBlob } from './fileActions';
import {
  type AvailableSize,
  clampToPage,
  displaySize,
  fitPageScale,
  fitWidthScale,
  fromPagePoint,
  nudge,
  type PageSize,
  type RelativePoint,
  type Rotation,
  rotateClockwise,
  rotateCounterClockwise,
  roundPoint,
  toPagePoint,
  zoomIn,
  zoomLabel,
  zoomOut,
} from './geometry';
import { noteKeyAction } from './noteKeys';
import type { PaperNote, PaperNotes } from './notes';
import type { PageSource, SourceKind, SourcePage } from './source';
import { useViewerSource } from './useViewerSource';

export function DocumentViewer({
  file,
  kind,
  notes,
  onExit,
  onNext,
}: {
  file: DocumentFile;
  kind: SourceKind;
  /** A papírra tűzött jegyzetek — nélküle a néző csak néz. */
  notes?: PaperNotes;
  /** `←` a bal szélen, vagy a semmit nem jelentő `Esc`: ki a papírból (#68). */
  onExit?: () => void;
  /** `→` a jobb szélen: tovább a döntés hasábjára (#68). */
  onNext?: () => void;
}) {
  const { t } = useTranslation();
  const state = useViewerSource(file.url, kind);

  if (state.status === 'loading') {
    return <p className="original__empty">{t('worklist.original.loading')}</p>;
  }

  if (state.status === 'failed') {
    return <p className="original__empty">{t('worklist.original.unavailable')}</p>;
  }

  // BIZONYLATVÁLTÁSKOR a forrás „töltés"-be megy, és a papír-doboz leválik:
  // az oldal, a nagyítás és a forgatás alapállásba áll — a következő papír
  // nem örökli az előző fordított állását (`useViewerSource`).
  return (
    <ViewerStage
      source={state.source}
      blob={state.blob}
      file={file}
      kind={kind}
      notes={notes}
      onExit={onExit}
      onNext={onNext}
    />
  );
}

/**
 * A GOMB BILLENTYŰJE, kiírva (#68). `aria-hidden`: a képernyőolvasó az
 * `aria-keyshortcuts`-ból tudja, és a gomb neve így nem lesz „Szélesség W".
 */
function Key({ children }: { children: string }) {
  return (
    <span className="viewer__key" aria-hidden="true">
      {children}
    </span>
  );
}

/** Egy nagyítási lépés ennyi összegyűlt görgetésért (egy egérgörgő-kattintás ~100). */
const WHEEL_STEP = 50;

/**
 * A papír-doboz osztálya: az „egész lap" nézetben nincs mit húzni, ott a
 * kurzor sem ígéri; nagyítva a kéz jelzi, hogy megfogható.
 */
function stageClass(zoom: Zoom, panning: boolean): string {
  if (panning) {
    return 'viewer__stage viewer__stage--panning';
  }
  return zoom === 'page' ? 'viewer__stage' : 'viewer__stage viewer__stage--pannable';
}

/** Ennyi elmozdulás alatt a lenyomás kattintás, nem húzás — a kéz remeg. */
const DRAG_THRESHOLD_PX = 4;

/**
 * A NAGYÍTÁS: szám, vagy a két illesztés egyike. Az illesztés KÖVETI a panelt
 * (átméretezés, teljes képernyő), a szám nem. Az első nézet az egész lap
 * (#68); a szélesség az olvasásé.
 */
type Zoom = number | 'page' | 'width';

function ViewerStage({
  source,
  blob,
  file,
  kind,
  notes,
  onExit,
  onNext,
}: {
  source: PageSource;
  blob: Blob;
  file: DocumentFile;
  kind: SourceKind;
  notes: PaperNotes | undefined;
  onExit: (() => void) | undefined;
  onNext: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const [pageNumber, setPageNumber] = useState(1);
  const [rotation, setRotation] = useState<Rotation>(0);
  const [zoom, setZoom] = useState<Zoom>('page');
  const [page, setPage] = useState<SourcePage | null>(null);
  const stage = useRef<HTMLDivElement | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const available = useContentBox(stage);
  /** A letétel alatt álló jelölő helye — a KÉPERNYŐN látott lapon. */
  const [placing, setPlacing] = useState<RelativePoint | null>(null);
  /**
   * KINEK a helye — `null`: új jelölő (`N`), kulcs: a hely nélküli jegyzet
   * „+"-a (#68). Az utóbbi letétele ÁTHELYEZÉS (`move`), nem új sor.
   */
  const [placingKey, setPlacingKey] = useState<string | null>(null);

  // A PAPÍR DOBOZA A JEGYZETEKÉ IS: a jegyzet mezőjéből ide tér vissza a fókusz.
  const paper = notes?.paper;
  const setStage = useCallback(
    (element: HTMLDivElement | null) => {
      stage.current = element;
      if (paper !== undefined) {
        paper.current = element;
      }
    },
    [paper]
  );

  /**
   * A KIJELÖLT JELÖLŐ OLDALÁRA LAPOZUNK — a `3` a 2. oldal jelölőjére is
   * ugrik. A KÉRÉS lapoz, nem a kiemelés állapota: ha a könyvelő utána
   * elóvakodik egy másik oldalra, a kiemelés nem rántja vissza — de a
   * következő `3` igen, akkor is, ha a jelölő végig kiemelve maradt.
   */
  const items = useRef<PaperNote[]>([]);
  items.current = notes?.items ?? [];
  const jump = notes?.jump ?? null;
  useEffect(() => {
    const item = items.current.find((note) => note.key === jump?.key);
    if (item !== undefined) {
      setPageNumber(Math.min(Math.max(1, item.pin.page), source.pageCount));
    }
  }, [jump, source.pageCount]);

  // ÉS A JELÖLŐ A LÁTÓTÉRBE KERÜL — nagyítva a lap nagy része a kereten kívül áll.
  // biome-ignore lint/correctness/useExhaustiveDependencies: az ugrás és az oldal a jel
  useEffect(() => {
    sheet.current
      ?.querySelector('[data-pin-active]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [jump, page]);

  useEffect(() => {
    let current = true;
    // AZ ÚJ OLDAL A TETEJÉN KEZDŐDIK. Az előző oldal aljáról `PageDown`-nal
    // érkezve különben az új oldal aljára esnénk.
    if (stage.current !== null) {
      stage.current.scrollTop = 0;
    }
    source
      .page(pageNumber)
      .then((loaded) => {
        if (current) {
          setPage(loaded);
        }
      })
      // EGY SÉRÜLT OLDAL nem állítja meg a nézőt: az előző rajz marad, a
      // többi oldal lapozható, a letöltés működik.
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [source, pageNumber]);

  const scale = page === null ? 1 : fitScale(zoom, page.size, rotation, available);

  useEffect(() => {
    if (page === null || canvas.current === null) {
      return;
    }
    const task = page.render(canvas.current, scale, rotation);
    // A RAJZOLÁS HIBÁJA nem állítja meg a munkát: a lap üres marad, a
    // letöltés és a „Megnyitom" továbbra is működik.
    task.promise.catch(() => undefined);
    return () => task.cancel();
  }, [page, scale, rotation]);

  const shown = page === null ? null : displaySize(page.size, scale, rotation);

  const startPlacing = (key: string | null = null) => {
    setPlacingKey(key);
    setPlacing(visibleCenter(stage.current, sheet.current));
    stage.current?.focus();
  };

  const cancelPlacing = () => {
    setPlacing(null);
    setPlacingKey(null);
  };

  const commitPlacing = (point: RelativePoint) => {
    const pin = { page: pageNumber, ...roundPoint(toPagePoint(point, rotation)) };
    cancelPlacing();
    if (placingKey === null) {
      notes?.place(pin);
      return;
    }
    notes?.move(placingKey, pin);
    // A LETETT jegyzet a kiemelt: most kapott számot, és a könyvelő látni
    // akarja, melyik lett az.
    notes?.select(placingKey);
  };

  // A TÉTELSOROK „+"-A a papíron indítja a letételt (#68). A KÉRÉS objektum,
  // nem kulcs: ugyanaz a „+" kétszer is kérheti.
  //
  // ⚠️ CSAK AMÍG A SOR MÉG HELY NÉLKÜLI. A kérés nem ürül ki: ha a néző
  // ugyanazon a bizonylaton újra felépül (a forrás újratölt), az effekt a
  // RÉGI kéréssel fut le még egyszer — és egy már letett sort vinne vissza
  // letételbe, ahonnan az `Enter` elmozdítaná.
  const placeRequest = notes?.placeRequest ?? null;
  const looseKey = notes?.loose?.key ?? null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: a kérés a jel; a `startPlacing` minden renderben új, a `looseKey` csak feltétel
  useEffect(() => {
    if (placeRequest !== null && placeRequest.key === looseKey) {
      startPlacing(placeRequest.key);
    }
  }, [placeRequest]);

  /** A papír billentyűi a jelölőkhöz. `true`, ha lekezelte — akkor tovább nem megy. */
  const applyNoteKey = (event: KeyboardEvent): boolean => {
    const action = notes === undefined ? null : noteKeyAction(event, placing !== null, notes);
    if (action === null || notes === undefined) {
      return false;
    }
    switch (action.kind) {
      case 'nudge':
        setPlacing((point) => (point === null ? null : nudge(point, action.dx, action.dy)));
        break;
      case 'commit':
        if (placing !== null) {
          commitPlacing(placing);
        }
        break;
      case 'cancel':
        cancelPlacing();
        break;
      case 'start':
        startPlacing();
        break;
      case 'select':
        notes.select(action.key);
        break;
      case 'edit':
        notes.edit(action.key);
        break;
      case 'swallow':
        break;
    }
    return true;
  };

  /**
   * A PAPÍR KÉZZEL HÚZHATÓ (#68, Viktor 10-20) — megfogod és arrébb tolod,
   * mint egy PDF-nézőben. Görgető gombok helyett: a görgő mellett ez a
   * nagyított papír természetes mozdulata.
   *
   * A küszöb alatt a lenyomás KATTINTÁS marad (kiemelés le, letétel) — ugyanaz
   * a szabály, mint a jelölő húzásánál. Letétel közben nincs húzás: ott a
   * kattintás tesz le. A jelölőt a saját húzása viszi, nem a papírt.
   */
  const pan = useRef<{ x: number; y: number; left: number; top: number; moved: boolean } | null>(
    null
  );
  /** A húzás végén a böngésző még egy `click`-et küld — az ne vegye le a kiemelést. */
  const justPanned = useRef(false);
  const [panning, setPanning] = useState(false);

  const pressStage = (event: PointerEvent<HTMLDivElement>) => {
    const box = stage.current;
    if (event.button !== 0 || placing !== null || box === null) {
      return;
    }
    if (event.target instanceof Element && event.target.closest('.viewer__pin') !== null) {
      return;
    }
    pan.current = {
      x: event.clientX,
      y: event.clientY,
      left: box.scrollLeft,
      top: box.scrollTop,
      moved: false,
    };
  };

  const dragStage = (event: PointerEvent<HTMLDivElement>) => {
    const start = pan.current;
    const box = stage.current;
    if (start === null || box === null) {
      return;
    }
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (!start.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
        return;
      }
      start.moved = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setPanning(true);
    }
    box.scrollLeft = start.left - dx;
    box.scrollTop = start.top - dy;
  };

  const releaseStage = () => {
    if (pan.current?.moved) {
      justPanned.current = true;
    }
    pan.current = null;
    setPanning(false);
  };

  /** A kattintás a papíron: letétel közben letesz, különben leveszi a kiemelést. */
  const clickPaper = (event: MouseEvent) => {
    if (justPanned.current) {
      justPanned.current = false;
      return;
    }
    if (notes === undefined || !(event.target instanceof Node)) {
      return;
    }
    if (!sheet.current?.contains(event.target)) {
      return;
    }
    if (placing === null) {
      notes.select(null);
      return;
    }
    const point = pointIn(sheet.current, event.clientX, event.clientY);
    if (point !== null) {
      commitPlacing(point);
    }
  };

  const actions = {
    zoomIn: () => setZoom(zoomIn(scale)),
    zoomOut: () => setZoom(zoomOut(scale)),
    fit: () => setZoom('page'),
    fitWidth: () => setZoom('width'),
    rotate: () => setRotation(rotateClockwise),
    rotateBack: () => setRotation(rotateCounterClockwise),
    previous: () => setPageNumber((n) => Math.max(1, n - 1)),
    next: () => setPageNumber((n) => Math.min(source.pageCount, n + 1)),
  };

  /**
   * CTRL + GÖRGŐ NAGYÍT (#68), mint minden nézőben — a görgő egyébként görget.
   *
   * ⚠️ NEM `onWheel`: a React görgő-figyelője passzív, és abban a
   * `preventDefault` hatástalan — a böngésző az EGÉSZ LAPOT nagyítaná a papír
   * helyett. Ezért saját, nem passzív figyelő.
   *
   * A görgetés ÖSSZEGYŰLIK egy lépésnyi küszöbig: az érintőpad csípése apró
   * értékek sorát küldi, és lépésenként egy-egy nagyítás a papírt a
   * 10 %-tól az 500 %-ig rántaná egyetlen mozdulattal.
   */
  const latest = useRef(actions);
  latest.current = actions;
  useEffect(() => {
    const box = stage.current;
    if (box === null) {
      return;
    }
    let gathered = 0;
    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) {
        return;
      }
      event.preventDefault();
      gathered += event.deltaY;
      if (Math.abs(gathered) < WHEEL_STEP) {
        return;
      }
      if (gathered < 0) {
        latest.current.zoomIn();
      } else {
        latest.current.zoomOut();
      }
      gathered = 0;
    };
    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
  }, []);

  // ⚠️ AZ ESZKÖZSOR GOMBJAI NEM `Tab`-ÁLLOMÁSOK (#68, Viktor 10-20). Mind
  // a nyolcnak van billentyűje, és a gombon ki is van írva; a `Tab` így a
  // hasábokon megy végig (kupac → papír → jelöltek → Tételsorok →
  // Lefűzöm), nem tíz gombon át. Egérrel ugyanúgy kattinthatók. A Letöltés
  // és a Nyomtatás marad: azoknak nincs billentyűje.
  return (
    <div className="viewer">
      <div className="viewer__toolbar" role="toolbar" aria-label={t('worklist.viewer.toolbar')}>
        <button
          type="button"
          onClick={actions.zoomOut}
          aria-label={t('worklist.viewer.zoomOut')}
          aria-keyshortcuts="-"
          tabIndex={-1}
        >
          −
        </button>
        {/* NEM `aria-live`: illesztett nézetben az érték a panel minden
            átméretezésére változik, és a képernyőolvasó kérés nélkül újra
            meg újra felolvasná. */}
        <span className="viewer__zoom">{zoomLabel(scale)}</span>
        <button
          type="button"
          onClick={actions.zoomIn}
          aria-label={t('worklist.viewer.zoomIn')}
          aria-keyshortcuts="+"
          tabIndex={-1}
        >
          +
        </button>
        <button
          type="button"
          onClick={actions.fit}
          aria-pressed={zoom === 'page'}
          aria-keyshortcuts="0"
          tabIndex={-1}
        >
          {t('worklist.viewer.fit')} <Key>0</Key>
        </button>
        <button
          type="button"
          onClick={actions.fitWidth}
          aria-pressed={zoom === 'width'}
          aria-keyshortcuts="W"
          tabIndex={-1}
        >
          {t('worklist.viewer.fitWidth')} <Key>W</Key>
        </button>
        <button type="button" onClick={actions.rotate} aria-keyshortcuts="R" tabIndex={-1}>
          {t('worklist.viewer.rotate')} <Key>R</Key>
        </button>
        {notes === undefined ? null : (
          <button
            type="button"
            onClick={() => (placing === null ? startPlacing() : cancelPlacing())}
            aria-pressed={placing !== null}
            aria-keyshortcuts="N"
            tabIndex={-1}
          >
            {t('worklist.viewer.addNote')} <Key>N</Key>
          </button>
        )}
        {source.pageCount > 1 ? (
          <span className="viewer__pages">
            <button
              type="button"
              onClick={actions.previous}
              disabled={pageNumber === 1}
              aria-label={t('worklist.viewer.previousPage')}
              aria-keyshortcuts="PageUp"
              title="PageUp"
              tabIndex={-1}
            >
              ‹
            </button>
            <span aria-live="polite">
              {t('worklist.viewer.pageOf', { page: pageNumber, count: source.pageCount })}
            </span>
            <button
              type="button"
              onClick={actions.next}
              disabled={pageNumber === source.pageCount}
              aria-label={t('worklist.viewer.nextPage')}
              aria-keyshortcuts="PageDown"
              title="PageDown"
              tabIndex={-1}
            >
              ›
            </button>
          </span>
        ) : null}
        <span className="viewer__spacer" />
        <button type="button" onClick={() => saveBlob(blob, file.originalFilename)}>
          {t('worklist.viewer.download')}
        </button>
        <button
          type="button"
          onClick={() =>
            printBlob(
              blob,
              embedType(file.mimeType) ?? (kind === 'pdf' ? 'application/pdf' : 'image/png')
            )
          }
        >
          {t('worklist.viewer.print')}
        </button>
      </div>

      {placing === null ? null : (
        <output className="viewer__hint">{t('worklist.viewer.placingHint')}</output>
      )}

      <div
        className={stageClass(zoom, panning)}
        ref={setStage}
        onPointerDown={pressStage}
        onPointerMove={dragStage}
        onPointerUp={releaseStage}
        onPointerCancel={releaseStage}
        onLostPointerCapture={releaseStage}
        // A PAPÍR FÓKUSZÁLHATÓ, mert a billentyűi itt élnek. A régi
        // `<iframe>`-et ki kellett venni a `Tab`-sorrendből, mert a böngésző
        // PDF-nézője elnyelte a `Tab`-ot; a canvas nem nyel el semmit.
        // biome-ignore lint/a11y/noNoninteractiveTabindex: görgetett doboz, billentyűzettel csak fókuszálva görgethető (axe: scrollable-region-focusable)
        tabIndex={0}
        // `application`: SAJÁT BILLENTYŰKKEL működő vezérlő. A képernyőolvasó
        // így a `+`, `−`, `R` leütést a nézőnek adja, nem a saját
        // böngésző-módjában értelmezi. A billentyűket a címke sorolja fel.
        role="application"
        aria-label={t(
          notes === undefined ? 'worklist.viewer.stage' : 'worklist.viewer.stageWithNotes',
          {
            name: file.originalFilename,
          }
        )}
        // A KATTINTÁS A DOBOZON FOGÓDIK, nem a lapon: a `application` szerepű
        // doboz interaktív, a lap `<div>`-je nem — és a jelölők a lapon belül
        // állnak, tehát a lap szélén kívüli kattintás úgyis kimarad.
        onClick={clickPaper}
        onKeyDown={(event) => {
          if (applyNoteKey(event)) {
            event.preventDefault();
            // Ugyanazért, mint a `handleKey`-ben: a munkalista ne kapja meg.
            event.stopPropagation();
            return;
          }
          if (onExit !== undefined && isExitKey(event, stage.current)) {
            event.preventDefault();
            event.stopPropagation();
            onExit();
            return;
          }
          // A `↑`/`↓` A PAPÍRON GÖRGET (Viktor, 10-20). A munkalista ablak-
          // szintű `↑`/`↓`-ja („előző/következő bizonylat") különben elkapná,
          // és a papír görgetése helyett bizonylatot váltana. A bizonylatok
          // közt a kupacon lehet lépni (`←`/`Esc` vissza), vagy `J`/`K`-val.
          //
          // A `←` IS, ha nem a bal szélen áll (ott fent kilép): a Tár ablak-
          // szintű `←`-je („a keresőbe", 10-26) különben elkapná, és a
          // nagyított papír görgetése helyett a keresőbe ugrana — teljes
          // képernyőn ráadásul egy láthatatlan mezőre. A `→` tükre.
          if (
            isBareKey(event, 'ArrowUp') ||
            isBareKey(event, 'ArrowDown') ||
            isBareKey(event, 'ArrowLeft')
          ) {
            event.stopPropagation();
            return;
          }
          // A `→` A PAPÍRON GÖRGET, a JOBB SZÉLÉN pedig továbblép a döntés
          // hasábjára (#68) — a `←` tükre. A munkalista `→`-je („belépés") az
          // ablakon figyel, és a görgetést elnyelné; ide nem érhet el.
          if (isBareKey(event, 'ArrowRight')) {
            event.stopPropagation();
            if (onNext !== undefined && !document.fullscreenElement && atRightEdge(stage.current)) {
              event.preventDefault();
              onNext();
            }
            return;
          }
          handleKey(event, actions, source.pageCount);
        }}
      >
        {/* A LAP ÉS A JELÖLŐK EGY DOBOZBAN, a canvas méretén: a jelölő helye
            így a doboz százaléka, és a nagyítás nem mozdítja el. */}
        <div
          ref={sheet}
          className={placing === null ? 'viewer__sheet' : 'viewer__sheet viewer__sheet--placing'}
          style={shown === null ? undefined : { width: shown.width, height: shown.height }}
        >
          <canvas
            ref={canvas}
            className="viewer__page"
            role="img"
            aria-label={t('worklist.viewer.pageImage', {
              name: file.originalFilename,
              page: pageNumber,
            })}
          />
          {notes === undefined || shown === null ? null : (
            <PinLayer
              notes={notes}
              pageNumber={pageNumber}
              rotation={rotation}
              placing={placing}
              placingKey={placingKey}
              onPlaceLoose={startPlacing}
              sheet={sheet}
            />
          )}
        </div>
      </div>
    </div>
  );
}

type Actions = Record<
  'zoomIn' | 'zoomOut' | 'fit' | 'fitWidth' | 'rotate' | 'rotateBack' | 'previous' | 'next',
  () => void
>;

/** A papír billentyűi. A `Ctrl`/`Cmd` kombinációk a böngészőé maradnak (nagyítás, mentés). */
const KEYS: Record<string, keyof Actions> = {
  '+': 'zoomIn',
  '=': 'zoomIn',
  '-': 'zoomOut',
  '0': 'fit',
  w: 'fitWidth',
  W: 'fitWidth',
  r: 'rotate',
  R: 'rotateBack',
  PageUp: 'previous',
  PageDown: 'next',
};

/**
 * KI A PAPÍRBÓL, VISSZA A KUPACRA (#68) — a `→` belépés párja.
 *
 * Az `Esc` itt már csak akkor jut el, ha a jelölők nem kérték maguknak
 * (kiemelés levétele, letétel elvetése): az `applyNoteKey` előbb fut.
 *
 * ⚠️ A `←` CSAK A BAL SZÉLEN. Nagyítva a nyíl a papírt görgeti, és ezt nem
 * vehetjük el; a szélén viszont nincs mit görgetnie, ott a „vissza" jelentés
 * a tiszta. Ugyanez a szabály él a mezőjavításban: a `←` a szöveg ELEJÉRŐL
 * lép az előző mezőre.
 */
function isExitKey(event: KeyboardEvent, stage: HTMLElement | null): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
    return false;
  }
  // ⚠️ TELJES KÉPERNYŐN NEM. Ott az `Esc` a teljes képernyőből lép ki (az
  // `App` „egy `Escape` egy lépés vissza"-ja, a panel ki is írja), és a
  // kupac a képernyőn kívül áll: a fókusz egy láthatatlan sorra kerülne.
  if (document.fullscreenElement) {
    return false;
  }
  if (event.key === 'Escape') {
    return true;
  }
  return event.key === 'ArrowLeft' && (stage?.scrollLeft ?? 0) <= 0;
}

/** Ez a billentyű, módosító nélkül — a `Shift`/`Ctrl`-os változat a böngészőé. */
function isBareKey(event: KeyboardEvent, key: string): boolean {
  return event.key === key && !(event.ctrlKey || event.metaKey || event.altKey || event.shiftKey);
}

/** A papír a jobb szélén áll — nincs mit jobbra görgetni (1 px tűréssel). */
function atRightEdge(stage: HTMLElement | null): boolean {
  if (stage === null) {
    return true;
  }
  return stage.scrollLeft + stage.clientWidth >= stage.scrollWidth - 1;
}

function handleKey(event: KeyboardEvent, actions: Actions, pageCount: number) {
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return;
  }
  const action = KEYS[event.key];
  if (action === undefined) {
    return;
  }
  // EGYOLDALAS papíron nincs mit lapozni — a `PageUp`/`PageDown` ott a
  // GÖRGETÉSÉ marad. A papír épp azért fókuszálható, hogy billentyűvel is
  // görgethető legyen.
  if ((action === 'previous' || action === 'next') && pageCount <= 1) {
    return;
  }
  event.preventDefault();
  // A MUNKALISTA rövidítései az ablakon figyelnek; ami itt lekezelődött,
  // az oda ne érjen el.
  event.stopPropagation();
  actions[action]();
}

/**
 * A JELÖLŐK RÉTEGE a lapon — a megjelenítés és a húzás.
 *
 * A HÚZÁS A VÉGÉN MENT, egy kéréssel: közben csak a rajz követi az egeret.
 * A lenyomás a küszöb alatt kattintás (kijelöl), fölötte húzás — a kéz remeg,
 * és egy kijelölés nem tolhatja el a jelölőt egy pixellel.
 */
function PinLayer({
  notes,
  pageNumber,
  rotation,
  placing,
  placingKey,
  onPlaceLoose,
  sheet,
}: {
  notes: PaperNotes;
  pageNumber: number;
  rotation: Rotation;
  placing: RelativePoint | null;
  placingKey: string | null;
  /** A sarok „+"-ára kattintás: a billentyűs letétel indul ennek a sornak. */
  onPlaceLoose: (key: string) => void;
  sheet: RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation();
  const [dragging, setDragging] = useState<{ key: string; point: RelativePoint } | null>(null);
  const press = useRef<{ key: string; x: number; y: number; moved: boolean } | null>(null);
  /** A húzás végén a böngésző még egy `click`-et küld — az ne jelöljön ki. */
  const justDragged = useRef(false);

  const pinAt = (point: RelativePoint) => ({
    page: pageNumber,
    ...roundPoint(toPagePoint(point, rotation)),
  });

  const pressPin = (event: PointerEvent<HTMLButtonElement>, key: string) => {
    if (event.button !== 0) {
      return;
    }
    event.currentTarget.setPointerCapture?.(event.pointerId);
    press.current = { key, x: event.clientX, y: event.clientY, moved: false };
  };

  const dragPin = (event: PointerEvent<HTMLButtonElement>, key: string) => {
    const start = press.current;
    if (start === null || start.key !== key) {
      return;
    }
    const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (!start.moved && distance < DRAG_THRESHOLD_PX) {
      return;
    }
    start.moved = true;
    const point = pointIn(sheet.current, event.clientX, event.clientY);
    if (point !== null) {
      setDragging({ key, point });
    }
  };

  const cancelDrag = () => {
    press.current = null;
    setDragging(null);
  };

  /** `true`, ha a húzás egy áthelyezéssel ért véget. */
  const dropPin = (event: PointerEvent<HTMLButtonElement>, key: string): boolean => {
    const start = press.current;
    press.current = null;
    if (start === null || start.key !== key || !start.moved) {
      return false;
    }
    justDragged.current = true;
    setDragging(null);
    const point = pointIn(sheet.current, event.clientX, event.clientY);
    if (point === null) {
      return false;
    }
    notes.move(key, pinAt(point));
    return true;
  };

  const loose = notes.loose !== null && placingKey !== notes.loose.key ? notes.loose : null;
  const looseHeld = loose !== null && dragging?.key === loose.key ? dragging.point : null;

  return (
    <>
      {notes.items
        .filter((note) => note.pin.page === pageNumber)
        .map((note) => {
          const at =
            dragging?.key === note.key ? dragging.point : fromPagePoint(note.pin, rotation);
          const active = note.key === notes.activeKey;
          return (
            <button
              key={note.key}
              type="button"
              className={active ? 'viewer__pin viewer__pin--active' : 'viewer__pin'}
              style={{ left: `${at.x * 100}%`, top: `${at.y * 100}%` }}
              // A JELÖLŐK NEM `Tab`-ÁLLOMÁSOK: billentyűzettel az `1–9` ugrik
              // rájuk, és egy sűrűn tűzött papír különben tíz leütéssel
              // nyújtaná a papírtól a Tételsorokig vezető utat.
              tabIndex={-1}
              aria-label={t('worklist.viewer.pin', {
                number: note.number,
                label: note.label || t('worklist.viewer.pinEmpty'),
              })}
              aria-current={active || undefined}
              data-pin-active={active || undefined}
              onPointerDown={(event) => pressPin(event, note.key)}
              onPointerMove={(event) => dragPin(event, note.key)}
              onPointerUp={(event) => dropPin(event, note.key)}
              // A MEGSZAKADT HÚZÁS (toll, rendszer-gesztus, lapozás közben)
              // nyomtalanul ér véget — különben a jelölő egy félúton hagyott
              // helyen ragadna, és a következő elengedés azt mentené.
              onPointerCancel={cancelDrag}
              onLostPointerCapture={cancelDrag}
              onClick={(event) => {
                // A papír kattintása a kiemelést venné le — ez a jelölőé.
                event.stopPropagation();
                if (justDragged.current) {
                  justDragged.current = false;
                  return;
                }
                notes.select(note.key);
              }}
            >
              {note.number}
            </button>
          );
        })}
      {/* A HELY NÉLKÜLI JEGYZET „+"-a (#68) — a lap jobb felső sarkában, MINDEN
          oldalon: oda tehető le, ahol a könyvelő épp áll. Ugyanaz a húzás,
          mint a számozott jelölőé, és a végén ugyanaz a `move` — a sor a
          helyével egy `PATCH`-et kap. Letétel közben nem áll a sarokban: a
          szaggatott célkereszt ő maga. */}
      {loose === null ? null : (
        <button
          type="button"
          className={
            looseHeld === null
              ? 'viewer__pin viewer__pin--loose viewer__pin--corner'
              : 'viewer__pin viewer__pin--loose'
          }
          style={
            looseHeld === null
              ? undefined
              : { left: `${looseHeld.x * 100}%`, top: `${looseHeld.y * 100}%` }
          }
          tabIndex={-1}
          title={t('worklist.viewer.pinLooseHint')}
          aria-label={t('worklist.viewer.pinLoose', { label: loose.label })}
          onPointerDown={(event) => pressPin(event, loose.key)}
          onPointerMove={(event) => dragPin(event, loose.key)}
          onPointerUp={(event) => {
            // A RÁHÚZOTT jegyzet is kiemelődik, mint a billentyűvel letett:
            // most kapott számot, és a könyvelő látni akarja, melyik lett az.
            if (dropPin(event, loose.key)) {
              notes.select(loose.key);
            }
          }}
          onPointerCancel={cancelDrag}
          onLostPointerCapture={cancelDrag}
          onClick={(event) => {
            event.stopPropagation();
            if (justDragged.current) {
              justDragged.current = false;
              return;
            }
            onPlaceLoose(loose.key);
          }}
        >
          +
        </button>
      )}
      {placing === null ? null : (
        <span
          className="viewer__pin viewer__pin--placing"
          style={{ left: `${placing.x * 100}%`, top: `${placing.y * 100}%` }}
          role="img"
          aria-label={t('worklist.viewer.pinPlacing', { number: notes.numberFor(pinAt(placing)) })}
        >
          {notes.numberFor(pinAt(placing))}
        </span>
      )}
    </>
  );
}

/** A kattintás helye a lap dobozán belül, relatívan — `null`, amíg a doboznak nincs mérete. */
function pointIn(
  element: HTMLElement | null,
  clientX: number,
  clientY: number
): RelativePoint | null {
  const box = element?.getBoundingClientRect();
  if (box === undefined || box.width <= 0 || box.height <= 0) {
    return null;
  }
  return clampToPage({ x: (clientX - box.left) / box.width, y: (clientY - box.top) / box.height });
}

/**
 * A CÉLKERESZT A LÁTHATÓ RÉSZ KÖZEPÉN indul, nem a lap közepén: nagyítva a
 * lap közepe a kereten kívül állhat, és a könyvelő a semmibe tenné le.
 */
function visibleCenter(stage: HTMLElement | null, sheet: HTMLElement | null): RelativePoint {
  const view = stage?.getBoundingClientRect();
  const paper = sheet?.getBoundingClientRect();
  if (view === undefined || paper === undefined || paper.width <= 0 || paper.height <= 0) {
    return { x: 0.5, y: 0.5 };
  }
  return clampToPage({
    x: (view.left + view.width / 2 - paper.left) / paper.width,
    y: (view.top + view.height / 2 - paper.top) / paper.height,
  });
}

function fitScale(
  zoom: Zoom,
  page: PageSize,
  rotation: Rotation,
  available: AvailableSize
): number {
  if (zoom === 'page') {
    return fitPageScale(page, rotation, available);
  }
  if (zoom === 'width') {
    return fitWidthScale(page, rotation, available.width);
  }
  return zoom;
}

/**
 * A PAPÍR RENDELKEZÉSRE ÁLLÓ HELYE — az illesztés mércéje.
 *
 * Változik: a munkalista hasábja átméretezhető, és a teljes képernyő is
 * ugyanezt a dobozt nagyítja fel. A `ResizeObserver` nélküli környezetben
 * (régi böngésző, teszt) egyszer mérünk, és ott marad.
 *
 * A MAGASSÁGBÓL 2, A SZÉLESSÉGBŐL 1 px-et elveszünk: a `clientWidth` és a
 * `clientHeight` egészre kerekít, a hasáb szélessége viszont tört (`clamp`
 * százalékkal). Ráhagyás nélkül a lap egy fél pixellel túllóghat, a megjelenő
 * gördítősáv elvenné a helyet — az illesztés újraszámolna, és a lap ide-oda
 * ugrálna.
 */
function useContentBox(element: React.RefObject<HTMLElement | null>): AvailableSize {
  const [size, setSize] = useState<AvailableSize>({ width: 0, height: 0 });

  useEffect(() => {
    const target = element.current;
    if (target === null) {
      return;
    }
    const measure = () => {
      const style = getComputedStyle(target);
      const across = padding(style.paddingLeft) + padding(style.paddingRight);
      const down = padding(style.paddingTop) + padding(style.paddingBottom);
      setSize({
        width: Math.max(0, target.clientWidth - across - 1),
        height: Math.max(0, target.clientHeight - down - 2),
      });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(target);
    return () => observer.disconnect();
  }, [element]);

  return size;
}

function padding(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}
